import { PaginatedDataDTO, PaginationDTO } from '@bottega52/commons-pagination';
import { createStyles, withStyles, WithStyles } from '@material-ui/core/styles';
import { DeleteOutline, SearchOutlined } from '@material-ui/icons';
import { Button } from '@mui/material';
import { Stack } from '@mui/system';
import { DataGrid, GridColumns, GridRenderCellParams } from '@mui/x-data-grid';
import * as React from "react";
import { CustomerContractNotificationRuleType, ICustomerContractNotificationRuleInDTO } from '../../repository/jago/model/input/ICustomerContractNotificationRuleInDTO';
import { ICustomerContractServiceInDTO } from '../../repository/jago/model/input/ICustomerContractServiceInDTO';
import translations from '../../translations/i18next';
import ReduxLanguage from '../ReduxLanguage';

const styles = createStyles({
  rowContainer: {
    display: 'flex',
    margin: 10,
    flexDirection: 'column'
  },
  rowBodyContainer: {
    padding: 15,
    minHeight: 100,
  },
  rowTitle: {
    fontWeight: 'bold'
  },
  logo: {
    width: '20%',
  },
});

export interface ICustomerContractNotificationsTableState {
}

export interface ICustomerContractNotificationsTableProps extends WithStyles<typeof styles>{
  customerContractNotificationRules: PaginatedDataDTO<ICustomerContractNotificationRuleInDTO>;
  tableColumns?: GridColumns<ICustomerContractNotificationRuleInDTO>;
  onRowClick?: (notificationRule: ICustomerContractNotificationRuleInDTO) => void;
  onFetchElementsOnPage: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  onDeleteNotificationRuleRequest?: (notificationRule: ICustomerContractNotificationRuleInDTO) => void;

  customerContractServices: ICustomerContractServiceInDTO[];
  rowsPerPage: number;
  rowHeight?: number;
  disableSelectionOnClick?: boolean;
  isLoading?: boolean;
  hideDeleteButton?: boolean;
  hideFooter?: boolean;
}

class CustomerContractNotificationsTable extends React.Component<ICustomerContractNotificationsTableProps, ICustomerContractNotificationsTableState> {
  private defaultColumns: GridColumns<ICustomerContractNotificationRuleInDTO>;

  formatDate(timestamp?: number): string {
    if (!timestamp) return 'N/A';
    return new Date(timestamp).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  }

  public render() {
    const { 
      customerContractNotificationRules, 
      hideFooter, 
      rowsPerPage, 
      disableSelectionOnClick, 
      onRowClick, 
      onPageSizeChange, 
      onFetchElementsOnPage, 
      tableColumns, 
      rowHeight, 
      onDeleteNotificationRuleRequest,
      customerContractServices
    } = this.props;

    this.defaultColumns = [
      {
        field: 'ruleType',
        headerName: translations.t('credits.notificationRuleType'),
        type: 'string',
        sortable: true,
        filterable: true,
        minWidth: 150,
        flex: 1,
        renderCell: (params: GridRenderCellParams<any>) => {
          const notificationRule = params.row;
          return (
            <div>
              <div style={{ fontWeight: 'bold', fontSize: 14, color: notificationRule.ruleType === CustomerContractNotificationRuleType.ONCE ? 'darkorange' : 'darkblue', marginBottom: 4 }}>
                {translations.t(`credits.notificationRuleType_${notificationRule.ruleType}`)}
              </div>
              {notificationRule.ruleType === CustomerContractNotificationRuleType.PERIODIC && notificationRule.intervalMonths && (
                <div style={{ fontSize: 13, color: 'gray' }}>
                  {translations.t(`credits.notificationIntervalMonths_${notificationRule.intervalMonths}`)}
                </div>
              )}
            </div>
          );
        }
      },
      {
        field: 'schedule',
        headerName: translations.t('credits.schedule'),
        type: 'string',
        sortable: true,
        filterable: true,
        minWidth: 200,
        flex: 1,
        renderCell: (params: GridRenderCellParams<any>) => {
          const notificationRule = params.row;
          if (notificationRule.ruleType === CustomerContractNotificationRuleType.ONCE) {
            return (
              <div>
                <div style={{ fontSize: 14, fontWeight: 'bold', marginBottom: 4 }}>{this.formatDate(notificationRule.specificDate)}</div>
              </div>
            );
          } else {
            return (
              <div>
                <div style={{ fontSize: 14, fontWeight: 'bold', marginBottom: 4 }}>
                  {translations.t('credits.notificationDayOfMonth')}: {notificationRule.dayOfMonth || 'N/A'}
                </div>
                <div style={{ fontSize: 13, color: 'gray' }}>
                  {translations.t('credits.nextRun')}:
                </div>
                <div style={{ fontSize: 13, color: 'gray' }}>
                  {this.formatDate(notificationRule.nextFireDate)}
                </div>
              </div>
            );
          }
        }
      },
      {
        field: 'associatedServices',
        headerName: translations.t('credits.notificationAssociatedServices'),
        type: 'string',
        sortable: true,
        filterable: true,
        minWidth: 250,
        flex: 2,
        renderCell: (params: GridRenderCellParams<any>) => {
          const notificationRule = params.row;
          const serviceIds = Array.isArray(notificationRule.customerContractServiceIds) 
            ? notificationRule.customerContractServiceIds 
            : [];
          
          if (serviceIds.length === 0) {
            return (
              <div>
                <div style={{ fontSize: 13, fontStyle: 'italic', color: 'gray' }}>
                  {translations.t('credits.generalContractNotification')}
                </div>
              </div>
            );
          }

          const foundServices = serviceIds
            .map(serviceId => customerContractServices.find(service => service.id === serviceId))
            .filter(service => service !== undefined);

          const maxToShow = 1;
          const servicesToShow = foundServices.slice(0, maxToShow);
          const remainingCount = foundServices.length - maxToShow;

          return (
            <div>
              {servicesToShow.map((service, index) => (
                <div key={service.id} style={{ marginBottom: index < servicesToShow.length - 1 ? 8 : 4 }}>
                  <div style={{ fontWeight: 'bold', fontSize: 14, marginBottom: 2 }}>
                    {service.groupFamily || 'N/A'}
                  </div>
                  <div style={{ fontSize: 13, marginBottom: 1 }}>
                    {service.subject || 'N/A'}
                  </div>
                  <div style={{ fontSize: 13, color: 'gray' }}>
                    {service.code || 'N/A'}
                  </div>
                </div>
              ))}
              {remainingCount > 0 && (
                <div style={{ fontSize: 13, color: 'gray', fontStyle: 'italic', marginTop: 4 }}>
                  {translations.t('credits.andMoreServices', { count: remainingCount })}
                </div>
              )}
            </div>
          );
        }
      },
      {
        field: 'emails',
        headerName: translations.t('credits.notificationEmails'),
        type: 'string',
        sortable: true,
        filterable: true,
        minWidth: 200,
        flex: 2,
        renderCell: (params: GridRenderCellParams<any>) => {
          const notificationRule = params.row;
          const emails = Array.isArray(notificationRule.emails) ? notificationRule.emails : [];
          const maxToShow = 2;
          return (
            <div>
              {emails.slice(0, maxToShow).map((email, index) => (
                <div key={index} style={{ fontSize: 13, marginBottom: 2 }}>{email}</div>
              ))}
              {emails.length > maxToShow && (
                <div style={{ fontSize: 13, color: 'gray', fontStyle: 'italic', marginTop: 4 }}>
                  {translations.t('credits.andMoreEmails', { count: emails.length - maxToShow })}
                </div>
              )}
            </div>
          );
        }
      },

      {
        field: 'actions',
        headerName: translations.t('forms.actions'),
        renderCell: (params: any) => {
          const notificationRule = params.row;
          return (
            <Button
              variant="contained"
              style={{
                backgroundColor: 'darkred',
                flexShrink: 0,
                marginRight: 20,
                width: 'fit-content',
                color: 'white',
                padding: 4,
                paddingLeft: 10,
                paddingRight: 10,
                fontWeight: 'bold'
              }}
              onClick={(e) => {e.stopPropagation(); onDeleteNotificationRuleRequest && onDeleteNotificationRuleRequest(notificationRule)}}
            >
              <DeleteOutline style={{ fontSize: 15 }}/>
              <h5 style={{ fontWeight: 'bold', margin: 0, marginLeft: 5 }} ><ReduxLanguage languageKey={"forms.delete"} /></h5>
            </Button>
          );
        },
        type: 'string',
        sortable: false,
        minWidth: 120,
      },
    ];

    return (
      <DataGrid
        getRowId={(d) => d.id}
        rows={customerContractNotificationRules.content || []}
        columns={tableColumns ? tableColumns: this.defaultColumns}
        rowHeight={rowHeight ? rowHeight: 100}
        onPageSizeChange={hideFooter ? null : (pageSize) => onPageSizeChange(pageSize)}
        onPageChange={hideFooter ? null : page => onFetchElementsOnPage(page)}
        page={hideFooter ? null : (customerContractNotificationRules?.pagination as PaginationDTO)?.number}
        rowCount={hideFooter ? null : (customerContractNotificationRules?.pagination as PaginationDTO)?.totalElements}
        pageSize={hideFooter ? null : rowsPerPage}
        hideFooter={hideFooter || false}
        loading={this.props.isLoading}
        onRowClick={row => onRowClick ? onRowClick(row.row) : null}
        paginationMode="server"
        disableSelectionOnClick={disableSelectionOnClick}
        componentsProps={{
          pagination: {
            labelRowsPerPage: translations.t('forms.rowsPerPage')
          }
        }}
        components={{
          NoRowsOverlay: () => (
            <Stack height="100%" alignItems="center" justifyContent="center">
              <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center'}}>
                <SearchOutlined style={{ alignSelf: 'center' }} />
                <h3 style={{ textAlign: 'center', fontWeight: 'bold', marginBottom: 0 }}><ReduxLanguage languageKey='credits.noNotificationRulesFound' /></h3>
                <h4 style={{ textAlign: 'center', marginTop: 0 }}><ReduxLanguage languageKey='credits.noNotificationRulesDescription' /></h4>
              </div>
            </Stack>
          ),
          NoResultsOverlay: () => (
            <Stack height="100%" alignItems="center" justifyContent="center">
              <h3 style={{ textAlign: 'center', fontWeight: 'bold', marginBottom: 0 }}><ReduxLanguage languageKey='credits.noNotificationRulesFound' /></h3>
              <h4 style={{ textAlign: 'center', marginTop: 0 }}><ReduxLanguage languageKey='credits.noNotificationRulesDescription' /></h4>
            </Stack>
          )
        }}
        sx={{
          '& .MuiDataGrid-cell:focus': {
            outline: 'none',
          },
        }}
      />
    );
  }
}

export default withStyles(styles)(CustomerContractNotificationsTable); 